export type EventOptionParticipationChoice = {
  choiceId: string;
  choiceLabel: string;
  groupId: string;
  groupTitle: string;
};

export type EventOptionRowParticipation = {
  optionCount: number;
  choices: EventOptionParticipationChoice[];
};

export type EventOptionParticipationByTargetId = Record<string, EventOptionRowParticipation>;

export type EventOptionParticipationIndex = {
  eventLines: EventOptionParticipationByTargetId;
  eventLabor: EventOptionParticipationByTargetId;
};

export const createEmptyEventOptionParticipationIndex = (): EventOptionParticipationIndex => ({
  eventLines: {},
  eventLabor: {},
});

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
);

const asTrimmedString = (value: unknown): string => (
  typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : ''
);

const finalizeParticipation = (
  targets: Map<string, Map<string, EventOptionParticipationChoice>>,
): EventOptionParticipationByTargetId => {
  const output: EventOptionParticipationByTargetId = {};
  targets.forEach((choicesById, targetId) => {
    const choices = Array.from(choicesById.values()).sort((left, right) => (
      `${left.groupTitle}\u0000${left.choiceLabel}`.localeCompare(
        `${right.groupTitle}\u0000${right.choiceLabel}`,
      )
    ));
    output[targetId] = {
      optionCount: choices.length,
      choices,
    };
  });
  return output;
};

/**
 * Builds both item/charge and labor marker indexes from the same compact
 * event-option query used for the workspace summary.
 */
export const buildEventOptionParticipationIndex = (
  groupRows: unknown,
): EventOptionParticipationIndex => {
  const eventLineTargets = new Map<string, Map<string, EventOptionParticipationChoice>>();
  const eventLaborTargets = new Map<string, Map<string, EventOptionParticipationChoice>>();
  const groups = Array.isArray(groupRows) ? groupRows : [];

  groups.forEach(groupValue => {
    const group = asRecord(groupValue);
    const groupId = asTrimmedString(group.event_option_group_id ?? group.group_id ?? group.id);
    const groupTitle = asTrimmedString(group.title) || 'Option group';
    const choices = Array.isArray(group.event_option_choices) ? group.event_option_choices : [];

    choices.forEach(choiceValue => {
      const choice = asRecord(choiceValue);
      if (asTrimmedString(choice.status).toLowerCase() === 'replaced') return;
      const choiceId = asTrimmedString(
        choice.event_option_choice_id ?? choice.choice_id ?? choice.id,
      );
      if (!choiceId) return;
      const markerChoice: EventOptionParticipationChoice = {
        choiceId,
        choiceLabel: asTrimmedString(choice.label) || 'Option',
        groupId,
        groupTitle,
      };
      const links = Array.isArray(choice.event_option_links) ? choice.event_option_links : [];

      links.forEach(linkValue => {
        const link = asRecord(linkValue);
        const targetId = asTrimmedString(link.target_id);
        const targetType = asTrimmedString(link.target_type);
        if (!targetId) return;
        const targets = targetType === 'event_line'
          ? eventLineTargets
          : targetType === 'event_labor'
            ? eventLaborTargets
            : null;
        if (!targets) return;
        const choicesById = targets.get(targetId) ?? new Map<string, EventOptionParticipationChoice>();
        choicesById.set(choiceId, markerChoice);
        targets.set(targetId, choicesById);
      });
    });
  });

  return {
    eventLines: finalizeParticipation(eventLineTargets),
    eventLabor: finalizeParticipation(eventLaborTargets),
  };
};

/**
 * Rollup rows can represent the same option across several underlying event
 * lines. Merge by choice ID so the visible count stays truthful.
 */
export const mergeEventOptionRowParticipation = (
  participationByTargetId: EventOptionParticipationByTargetId | null | undefined,
  targetIds: Iterable<string | null | undefined>,
): EventOptionRowParticipation | null => {
  if (!participationByTargetId) return null;
  const choicesById = new Map<string, EventOptionParticipationChoice>();
  for (const rawTargetId of targetIds) {
    const targetId = asTrimmedString(rawTargetId);
    if (!targetId) continue;
    participationByTargetId[targetId]?.choices.forEach(choice => {
      choicesById.set(choice.choiceId, choice);
    });
  }
  if (!choicesById.size) return null;
  const choices = Array.from(choicesById.values()).sort((left, right) => (
    `${left.groupTitle}\u0000${left.choiceLabel}`.localeCompare(
      `${right.groupTitle}\u0000${right.choiceLabel}`,
    )
  ));
  return {
    optionCount: choices.length,
    choices,
  };
};
